import { CheckCircle, AlertCircle, XCircle, HelpCircle } from 'lucide-react';

interface StatusCardProps {
  title: string;
  status: 'healthy' | 'degraded' | 'error' | 'unknown' | 'unavailable';
  details?: Record<string, string | number | boolean>;
  note?: string;
}

const statusConfig = {
  healthy: {
    icon: CheckCircle,
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-700 dark:text-green-400',
    iconColor: 'text-green-500 dark:text-green-400',
    label: 'Healthy',
  },
  degraded: {
    icon: AlertCircle,
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    text: 'text-yellow-700 dark:text-yellow-400',
    iconColor: 'text-yellow-500 dark:text-yellow-400',
    label: 'Degraded',
  },
  error: {
    icon: XCircle,
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-700 dark:text-red-400',
    iconColor: 'text-red-500 dark:text-red-400',
    label: 'Error',
  },
  unknown: {
    icon: HelpCircle,
    bg: 'bg-gray-50 dark:bg-brand-navy',
    border: 'border-gray-200 dark:border-gray-700',
    text: 'text-gray-700 dark:text-gray-300',
    iconColor: 'text-gray-500 dark:text-gray-400',
    label: 'Unknown',
  },
  unavailable: {
    icon: XCircle,
    bg: 'bg-gray-50 dark:bg-brand-navy',
    border: 'border-gray-200 dark:border-gray-700',
    text: 'text-gray-500 dark:text-gray-400',
    iconColor: 'text-gray-400 dark:text-gray-500',
    label: 'Unavailable',
  },
};

export function StatusCard({ title, status, details, note }: StatusCardProps) {
  const config = statusConfig[status] || statusConfig.unknown;
  const Icon = config.icon;

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} p-4`}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${config.iconColor}`} />
          <span className={`text-sm font-medium ${config.text}`}>
            {config.label}
          </span>
        </div>
      </div>

      {details && Object.keys(details).length > 0 && (
        <div className="mt-3 space-y-1">
          {Object.entries(details).map(([key, value]) => (
            <div key={key} className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">{key}:</span>
              <span className="text-gray-700 dark:text-gray-200 font-medium">
                {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
              </span>
            </div>
          ))}
        </div>
      )}

      {note && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">{note}</p>
      )}
    </div>
  );
}
